import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  TableColumn,
  Button,
  PageHeader,
  Spinner,
  Input,
  Select,
  Avatar,
} from '../../components';
import type { SelectOption } from '../../components';
import {
  PlusIcon,
  UploadIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
  EyeOpenIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  CheckIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import { Lead, LeadStatus, LEAD_IMPORT_ACCEPT, TaskAssigneeOption, isLeadImportFile } from '../../types/lead';
import { useLeadStore } from '../../store/leadStore';
import { useToastStore } from '../../store/toastStore';
import { leadService } from '../../services/leadService';
import { LeadFormPanel, LeadFormPanelMode } from './LeadFormPanel';
import { LeadViewPanel } from './LeadViewPanel';
import { useAuthStore } from '../../store/authStore';
import {
  canCreateLead,
  canDeleteLead,
  canEditLead,
  canFollowUpLead,
  canImportLeads,
} from '../../utils/rbac';
import { formatApiError } from '../../utils/errorHandler';
import './LeadsList.scss';

const getActionErrorMessage = (err: unknown, fallback: string): string =>
  formatApiError(err).trim() || fallback;

type PanelState =
  | { open: false }
  | { open: true; mode: LeadFormPanelMode; leadId?: string };

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'Status' },
  { value: 'new_lead', label: 'New Lead' },
  { value: 'lead', label: 'Lead' },
  { value: 'progress', label: 'Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'pending', label: 'Pending' },
  { value: 'converted', label: 'Converted' },
];

const GENDER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'Gender' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
];

const SOURCE_PILL_COLORS = [
  'leads-list__pill--organic',
  'leads-list__pill--facebook',
  'leads-list__pill--instagram',
  'leads-list__pill--referral',
  'leads-list__pill--walkin',
];

const STATUS_PILL_CLASS: Record<LeadStatus, string> = {
  new_lead: 'leads-list__pill--lead',
  lead: 'leads-list__pill--lead',
  progress: 'leads-list__pill--progress',
  qualified: 'leads-list__pill--qualified',
  pending: 'leads-list__pill--progress',
  converted: 'leads-list__pill--converted',
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  new_lead: 'NEW LEAD',
  lead: 'LEAD',
  progress: 'PROGRESS',
  qualified: 'QUALIFIED',
  pending: 'PENDING',
  converted: 'CONVERTED',
};

const sourcePillClass = (name?: string): string => {
  if (!name) return SOURCE_PILL_COLORS[0];
  const index = Math.abs(name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)) % SOURCE_PILL_COLORS.length;
  return SOURCE_PILL_COLORS[index];
};

export const LeadsList: React.FC = () => {
  const role = useAuthStore((state) => state.role);
  const allowCreate = canCreateLead(role);
  const allowEdit = canEditLead(role);
  const allowDelete = canDeleteLead(role);
  const allowImport = canImportLeads(role);
  const allowFollowUp = canFollowUpLead(role);
  const [searchParams] = useSearchParams();
  const [panel, setPanel] = useState<PanelState>({ open: false });
  const [viewLeadId, setViewLeadId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [taskAssigneeId, setTaskAssigneeId] = useState('');
  const [taskAssignees, setTaskAssignees] = useState<TaskAssigneeOption[]>([]);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const {
    leads,
    currentPage,
    pageSize,
    total,
    totalPages,
    statusFilter,
    genderFilter,
    leadTypeFilter,
    leadSources,
    searchQuery,
    isLoading,
    importingLeads,
    exportingLeads,
    creatingTasks,
    deletingLeadId,
    error,
    fetchLeads,
    fetchLeadSources,
    setPage,
    setPageSize,
    setStatusFilter,
    setGenderFilter,
    setLeadTypeFilter,
    setSearchQuery,
    clearFilters,
    deleteLead,
    bulkDelete,
    bulkCreateTasks,
    importLeads,
    exportLeads,
    clearError,
  } = useLeadStore();
  const showSuccessMessage = useToastStore(state => state.showSuccessMessage);
  const showErrorMessage = useToastStore(state => state.showErrorMessage);

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get('search')?.trim() ?? ''
  );
  const viewParam = searchParams.get('view')?.trim() ?? '';

  const leadTypeOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'all', label: 'Lead Type' },
      ...leadSources.map((source) => ({
        value: String(source.id),
        label: source.name,
      })),
    ],
    [leadSources]
  );

  useEffect(() => {
    fetchLeadSources();
    const initialSearch = searchParams.get('search')?.trim() ?? '';
    if (initialSearch) {
      setSearchQuery(initialSearch);
      return;
    }
    fetchLeads().catch((err) => {
      showErrorMessage({
        title: 'Failed to load leads',
        message: getActionErrorMessage(err, 'Unable to load leads.'),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSelection = selectedIds.size > 0;

  useEffect(() => {
    if (!hasSelection) return;

    let cancelled = false;
    leadService
      .getTaskAssignees()
      .then((assignees) => {
        if (!cancelled) setTaskAssignees(assignees);
      })
      .catch((err) => {
        if (cancelled) return;
        setTaskAssignees([]);
        setTaskAssigneeId('');
        showErrorMessage({
          title: 'Failed to load assignees',
          message: getActionErrorMessage(err, 'Unable to load task owners.'),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [hasSelection, showErrorMessage]);

  const taskAssigneeOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'Unassigned' },
      ...taskAssignees.map((assignee) => ({
        value: String(assignee.userId),
        label: assignee.name,
      })),
    ],
    [taskAssignees]
  );

  useEffect(() => {
    if (!viewParam) return;
    setViewLeadId(viewParam);
  }, [viewParam]);

  const startEntry = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, total);

  const getFullName = useCallback((lead: Lead): string => lead.fullName, []);

  const openLeadPanel = useCallback((leadId: string) => {
    setPanel({ open: true, mode: 'edit', leadId });
  }, []);

  const openFollowUpPanel = useCallback((leadId: string) => {
    setPanel({ open: true, mode: 'follow_up', leadId });
  }, []);

  const handleEditFromView = useCallback(
    (leadId: string) => {
      setViewLeadId(null);
      if (allowEdit) {
        openLeadPanel(leadId);
      } else if (allowFollowUp) {
        openFollowUpPanel(leadId);
      }
    },
    [allowEdit, allowFollowUp, openLeadPanel, openFollowUpPanel]
  );

  const openLeadView = useCallback((leadId: string) => {
    setViewLeadId(leadId);
  }, []);

  const handleCloseLeadView = useCallback(() => {
    setViewLeadId(null);
  }, []);

  const allVisibleSelected =
    leads.length > 0 && leads.every((l) => selectedIds.has(l.leadId));

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        leads.forEach((l) => next.delete(l.leadId));
      } else {
        leads.forEach((l) => next.add(l.leadId));
      }
      return next;
    });
  }, [allVisibleSelected, leads]);

  const handleToggleSelect = useCallback((leadId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const handleDeleteLead = useCallback(
    async (leadId: string) => {
      if (!window.confirm('Are you sure you want to delete this lead?')) return;
      try {
        await deleteLead(leadId);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(leadId);
          return next;
        });
      } catch (err) {
        showErrorMessage({
          title: 'Failed to delete lead',
          message: getActionErrorMessage(err, 'Unable to delete this lead.'),
        });
      }
    },
    [deleteLead, showErrorMessage]
  );

  const handleBulkDelete = useCallback(async () => {
    if (
      !window.confirm(
        `Delete ${selectedIds.size} selected lead${selectedIds.size === 1 ? '' : 's'}?`
      )
    ) {
      return;
    }
    try {
      await bulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (err) {
      showErrorMessage({
        title: 'Failed to delete leads',
        message: getActionErrorMessage(err, 'Unable to delete selected leads.'),
      });
    }
  }, [bulkDelete, selectedIds, showErrorMessage]);

  const handleBulkExport = useCallback(async () => {
    try {
      await exportLeads(Array.from(selectedIds));
    } catch (err) {
      showErrorMessage({
        title: 'Failed to export leads',
        message: getActionErrorMessage(err, 'Unable to export leads.'),
      });
    }
  }, [exportLeads, selectedIds, showErrorMessage]);

  const handleImportFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      clearError();
      setImportMessage(null);
      if (!isLeadImportFile(file.name)) {
        setImportMessage('Please upload a CSV or Excel file (.csv, .xlsx, .xls).');
        event.target.value = '';
        return;
      }
      try {
        const result = await importLeads(file);
        const errorDetails =
          result.errors.length > 0
            ? ` ${result.errors
                .slice(0, 5)
                .map((entry) => `Row ${entry.row}: ${entry.message}`)
                .join('. ')}${result.errors.length > 5 ? '…' : ''}`
            : '';
        setImportMessage(
          `Imported ${result.imported} lead${result.imported === 1 ? '' : 's'}. Failed: ${result.failed}.${errorDetails}`
        );
      } catch (err) {
        showErrorMessage({
          title: 'Failed to import leads',
          message: getActionErrorMessage(err, 'Unable to import leads.'),
        });
      } finally {
        event.target.value = '';
      }
    },
    [clearError, importLeads, showErrorMessage]
  );

  const handleSearchSubmit = useCallback(() => {
    setSearchQuery(searchInput);
  }, [searchInput, setSearchQuery]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(searchInput.trim()) ||
    statusFilter !== 'all' ||
    genderFilter !== 'all' ||
    leadTypeFilter !== 'all';

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    clearFilters();
  }, [clearFilters]);

  const handleBulkCreateTask = useCallback(async () => {
    const parsedAssigneeId = taskAssigneeId ? Number(taskAssigneeId) : undefined;
    const assigneeUserId =
      parsedAssigneeId != null && Number.isFinite(parsedAssigneeId)
        ? parsedAssigneeId
        : undefined;

    try {
      const result = await bulkCreateTasks(Array.from(selectedIds), assigneeUserId);
      const createdCount = result.createdLeadIds?.length ?? 0;
      const skippedCount = result.skippedLeadIds?.length ?? 0;
      const messageParts: string[] = [];

      if (createdCount > 0) {
        messageParts.push(
          `Created tasks for ${createdCount} lead${createdCount === 1 ? '' : 's'}.`
        );
      }
      if (skippedCount > 0) {
        messageParts.push(
          `${skippedCount} lead${skippedCount === 1 ? ' was' : 's were'} skipped.`
        );
      }
      if (messageParts.length === 0) {
        messageParts.push('No tasks were created for the selected leads.');
      }

      showSuccessMessage({
        title: 'Task created successfully',
        message: messageParts.join(' '),
      });
      setSelectedIds(new Set());
      setTaskAssigneeId('');
    } catch (err) {
      showErrorMessage({
        title: 'Failed to create task',
        message: getActionErrorMessage(err, 'Unable to create tasks for selected leads.'),
      });
      return;
    }

    try {
      await fetchLeads();
    } catch (err) {
      showErrorMessage({
        title: 'Failed to load leads',
        message: getActionErrorMessage(err, 'Unable to load leads.'),
      });
    }
  }, [
    bulkCreateTasks,
    fetchLeads,
    selectedIds,
    showErrorMessage,
    showSuccessMessage,
    taskAssigneeId,
  ]);

  const getPageNumbers = useCallback((): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [1];
    if (currentPage <= 4) {
      pages.push(2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const columns: TableColumn<Lead>[] = useMemo(
    () => [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            className="leads-list__checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleSelectAll}
            aria-label="Select all leads"
          />
        ),
        width: '48px',
        render: (lead) => (
          <input
            type="checkbox"
            className="leads-list__checkbox"
            checked={selectedIds.has(lead.leadId)}
            onChange={() => handleToggleSelect(lead.leadId)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${getFullName(lead)}`}
          />
        ),
      },
      {
        key: 'name',
        header: 'Name',
        render: (lead) => (
          <span className="leads-list__name">{getFullName(lead)}</span>
        ),
      },
      {
        key: 'email',
        header: 'Email Address',
        render: (lead) => (
          <span className="leads-list__cell-text">{lead.email}</span>
        ),
      },
      {
        key: 'phoneNumber',
        header: 'Mobile Number',
        render: (lead) => (
          <span className="leads-list__cell-text">{lead.phoneNumber ?? '—'}</span>
        ),
      },
      {
        key: 'leadType',
        header: 'Lead Type',
        render: (lead) => (
          <span
            className={`leads-list__pill ${sourcePillClass(lead.leadSource?.name)}`}
          >
            {(lead.leadSource?.name ?? 'Unknown').toUpperCase()}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (lead) => (
          <span
            className={`leads-list__pill ${STATUS_PILL_CLASS[lead.status]}`}
          >
            {STATUS_LABELS[lead.status]}
          </span>
        ),
      },
      {
        key: 'taskAssignee',
        header: 'Assignee',
        width: '110px',
        render: (lead) => {
          const name = lead.taskAssignee?.name?.trim();
          if (!lead.taskAssignee || !name) {
            return <span className="leads-list__cell-text">—</span>;
          }

          return (
            <span className="leads-list__assignee" title={name}>
              <Avatar
                name={name}
                src={lead.taskAssignee.avatarUrl}
                size="small"
                alt={name}
              />
            </span>
          );
        },
      },
      {
        key: 'actions',
        header: 'Actions',
        align: 'right',
        width: '120px',
        render: (lead) => {
          const isDeleting = deletingLeadId === lead.leadId;
          return (
            <div
              className="leads-list__actions"
              onClick={(e) => e.stopPropagation()}
            >
              {allowEdit && (
              <button
                type="button"
                className="leads-list__action-btn"
                title="Edit lead"
                onClick={() => openLeadPanel(lead.leadId)}
              >
                <Pencil1Icon />
              </button>
              )}
              {allowFollowUp && !allowEdit && (
              <button
                type="button"
                className="leads-list__action-btn"
                title="Follow up on lead"
                onClick={() => openFollowUpPanel(lead.leadId)}
              >
                <Pencil1Icon />
              </button>
              )}
              <button
                type="button"
                className="leads-list__action-btn"
                title="View lead"
                onClick={() => openLeadView(lead.leadId)}
              >
                <EyeOpenIcon />
              </button>
              {allowDelete && (
              <button
                type="button"
                className="leads-list__action-btn"
                title="Delete lead"
                disabled={isDeleting}
                onClick={() => handleDeleteLead(lead.leadId)}
              >
                {isDeleting ? <Spinner size="small" /> : <TrashIcon />}
              </button>
              )}
            </div>
          );
        },
      },
    ],
    [
      allVisibleSelected,
      handleToggleSelectAll,
      selectedIds,
      getFullName,
      handleToggleSelect,
      allowDelete,
      allowEdit,
      allowFollowUp,
      openFollowUpPanel,
      handleDeleteLead,
      deletingLeadId,
      openLeadPanel,
      openLeadView,
    ]
  );

  const handleCreateLead = useCallback(() => {
    setPanel({ open: true, mode: 'create' });
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanel({ open: false });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  return (
    <div className="leads-list">
      <PageHeader
        title="Profile/Lead Management"
        subtitle="Track and convert your prospects through the pipeline."
        actions={
          (allowCreate || allowImport) ? (
          <div className="leads-list__header-actions">
            {allowImport && (
            <>
            <input
              ref={importInputRef}
              type="file"
              accept={LEAD_IMPORT_ACCEPT}
              hidden
              onChange={handleImportFile}
            />
            <Button
              variant="secondary"
              className="leads-list__import-btn"
              disabled={importingLeads}
              onClick={() => importInputRef.current?.click()}
            >
              {importingLeads ? <Spinner size="small" /> : <UploadIcon />}
              {importingLeads ? 'Importing…' : 'Import'}
            </Button>
            </>
            )}
            {allowCreate && (
            <Button className="leads-list__add-btn" onClick={handleCreateLead}>
              <PlusIcon />
              Add New Lead
            </Button>
            )}
          </div>
          ) : undefined
        }
      />

      {error && <div className="error-message">{error.message}</div>}
      {importMessage && (
        <div className="leads-list__import-message" role="status">
          {importMessage}
        </div>
      )}

      <div className="leads-list__card">
        <div className="leads-list__filters">
          <div className="leads-list__search-field leads-list__search-field--wide">
            <MagnifyingGlassIcon
              className="leads-list__field-icon"
              aria-hidden="true"
            />
            <Input
              placeholder="Search by name, email, or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onBlur={handleSearchSubmit}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              className="leads-list__search-input"
            />
          </div>

          <div className="leads-list__filter-select">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
              searchable={false}
              className="leads-list__select"
            />
          </div>

          <div className="leads-list__filter-select">
            <Select
              value={genderFilter}
              onChange={setGenderFilter}
              options={GENDER_OPTIONS}
              searchable={false}
              className="leads-list__select"
            />
          </div>

          <div className="leads-list__filter-select">
            <Select
              value={leadTypeFilter}
              onChange={setLeadTypeFilter}
              options={leadTypeOptions}
              searchable={false}
              className="leads-list__select"
            />
          </div>

          <button
            type="button"
            className="leads-list__clear-filters"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
          >
            Clear All
          </button>
        </div>

        <Table
          className="leads-list__table"
          columns={columns}
          data={leads}
          isLoading={isLoading}
          emptyMessage="No leads found"
        />

        {totalPages > 0 && (
          <div className="leads-list__pagination">
            <div className="leads-list__pagination-summary">
              Showing {startEntry}-{endEntry} of {total.toLocaleString()}
            </div>

            <div className="leads-list__page-size">
              <span>Items per page:</span>
              <Select
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={PAGE_SIZE_OPTIONS}
                searchable={false}
                fullWidth={false}
                className="leads-list__page-size-select"
              />
            </div>

            <div className="leads-list__pagination-controls">
              <button
                type="button"
                className="leads-list__page-btn"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeftIcon />
              </button>

              <div className="leads-list__page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="leads-list__page-ellipsis"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      type="button"
                      className={`leads-list__page-btn leads-list__page-btn--number ${page === currentPage ? 'leads-list__page-btn--active' : ''}`}
                      onClick={() => setPage(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                type="button"
                className="leads-list__page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                aria-label="Next page"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="leads-list__selection-bar">
          <span className="leads-list__selection-count">
            {selectedIds.size} lead{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="leads-list__selection-assignee">
            <Select
              label="Task owner"
              value={taskAssigneeId}
              onChange={setTaskAssigneeId}
              options={taskAssigneeOptions}
              searchable={false}
              disabled={creatingTasks}
              className="leads-list__selection-assignee-select"
            />
          </div>
          <div className="leads-list__selection-actions">
            <button
              type="button"
              className="leads-list__selection-action"
              disabled={creatingTasks}
              onClick={handleBulkCreateTask}
            >
              {creatingTasks ? <Spinner size="small" /> : <CheckIcon />}
              {creatingTasks ? 'Creating…' : 'Create Task'}
            </button>
            <button
              type="button"
              className="leads-list__selection-action"
              disabled={exportingLeads}
              onClick={handleBulkExport}
            >
              {exportingLeads ? <Spinner size="small" /> : <DownloadIcon />}
              {exportingLeads ? 'Exporting…' : 'Export'}
            </button>
            {allowDelete && (
            <button
              type="button"
              className="leads-list__selection-action leads-list__selection-action--danger"
              onClick={handleBulkDelete}
            >
              <TrashIcon />
              Delete
            </button>
            )}
          </div>
          <button
            type="button"
            className="leads-list__selection-close"
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <Cross2Icon />
          </button>
        </div>
      )}

      <LeadFormPanel
        isOpen={panel.open}
        mode={panel.open ? panel.mode : 'create'}
        leadId={panel.open ? panel.leadId : undefined}
        onClose={handleClosePanel}
      />

      <LeadViewPanel
        isOpen={viewLeadId !== null}
        leadId={viewLeadId ?? undefined}
        onClose={handleCloseLeadView}
        onEdit={allowEdit ? handleEditFromView : undefined}
        onFollowUp={allowFollowUp && !allowEdit ? handleEditFromView : undefined}
      />
    </div>
  );
};
